<template>
  <Layout hide-route-header>
    <div class="max-w-3xl mx-auto">
      <Card>
        <div v-if="isLoading">
          <div class="spinner"></div>
        </div>
        <div v-else>
          <header class="text-lg font-bold text-gray-800">Upload Photos for Social Media Ads</header>
          <p class="my-4">
            Please upload at least 3-5 pictures of your team below. These photos do not have to be professional quality.
            The more candid the better.
          </p>
          <p class="text-gray-500 text-sm">Think team meetings, happy hours, holiday parties, etc.</p>
          <FileInput
            class="mb-12"
            name="files"
            limits="any file"
            :multiple="true"
            accept="image/*"
            :model-value="teamPhotos"
            @update:model-value="photosUploaded"
          />
          <div class="flex justify-center">
            <TButton color="indigo" :disabled="isUploadDisabled" @click="submitPhotos">Upload</TButton>
          </div>
        </div>
      </Card>
    </div>
  </Layout>
</template>

<script setup lang="ts">
import Card from '../../../components/Card/index.vue';
import { onMounted, ref } from 'vue';
import Layout from '../../../components/Layout/index.vue';
import TButton from '@/components/shared/TButton.vue';
import type { FacebookAdConfig } from '@/api/quickstart';
//@ts-ignore
import { useStore } from 'vuex';
import { Api } from '@/api';
import useMetaCoordinator from './useMetaCoordinator';
import { useRouter } from 'vue-router';

const { enforcePageAccess } = useMetaCoordinator();
const store = useStore();
const router = useRouter();

const facebookAdConfig = ref<FacebookAdConfig | null>(null);
const teamPhotos = ref<FileList | null>(null);
const isUploadDisabled = ref(true);
const isLoading = ref(true);

const photosUploaded = (photoList: FileList) => {
  teamPhotos.value = photoList;
  isUploadDisabled.value = false;
};

const submitPhotos = async () => {
  const errors = [];

  if (facebookAdConfig.value && teamPhotos.value) {
    const result = await Api.Quickstart.updateFacebookAdConfig({
      teamPhotos: teamPhotos.value,
    });
    if (result.status === 'error') errors.push(result.message || 'Unknown network error.');
  } else {
    if (!facebookAdConfig.value) errors.push("Team's facebook advertising configuration was not found.");
    if (!teamPhotos.value) errors.push('Submission attempted without any photos.');
  }

  if (errors.length > 0) {
    store.commit('general/showAlert', {
      alertType: 'error',
      alertTitle: 'Photo upload error',
      alertDescription: errors.join(', '),
    });
  } else {
    router.push('/quickstart/meta/access');
    store.commit('general/showAlert', {
      alertType: 'success',
      alertTitle: 'Photos successfully uploaded',
      alertDescription: 'Congrats, you have uploaded your social photos!',
    });
  }
};

onMounted(async () => {
  const facebookAdConfigResponse = await Api.Quickstart.getFacebookAdConfig();
  isLoading.value = false;
  await enforcePageAccess(facebookAdConfigResponse);
  if (facebookAdConfigResponse?.record) facebookAdConfig.value = facebookAdConfigResponse.record;
});
</script>
